import { Check, Minus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { TEAM_ACTIONS, type TeamAction } from "@/lib/team/catalog";
import type { MatrixGroup } from "./permissionMatrixModel";

type Props = {
  groups: MatrixGroup[];
  disabled: boolean;
  onToggle: (feature: string, action: TeamAction, next: boolean) => void;
};

const grid = "grid grid-cols-[minmax(220px,1fr)_repeat(4,88px)] items-center";

export function PermissionMatrix({ groups, disabled, onToggle }: Props) {
  const { t } = useTranslation("team");
  return (
    <div className="overflow-x-auto">
      <div role="table" aria-label={t("permissions.matrixTitle")} className="min-w-[620px]">
        <div role="row" className={cn(grid, "min-h-10 border-b border-border bg-muted text-xs font-medium text-muted-foreground")}>
          <span role="columnheader" className="sticky left-0 bg-muted px-6">{t("permissions.area")}</span>
          {TEAM_ACTIONS.map((a) => (
            <span role="columnheader" key={a} className="text-center">{t(`actions.${a}`)}</span>
          ))}
        </div>
        {groups.map((group) => (
          <div role="rowgroup" key={group.module}>
            <div role="row" className="border-b border-border bg-muted px-6 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <span role="cell">{t(`modules.${group.module}`)}</span>
            </div>
            {[...group.rows]
              .sort((a, b) => t(`features.${a.feature}.name`).localeCompare(t(`features.${b.feature}.name`)))
              .map((row) => {
                const area = t(`features.${row.feature}.name`);
                return (
                  <div role="row" key={row.feature} className={cn(grid, "border-b border-border text-sm last:border-b-0")}>
                    <div role="cell" className="sticky left-0 flex flex-col gap-0.5 bg-card px-6 py-2">
                      <span className="font-medium text-foreground">{area}</span>
                      <span className="text-xs text-muted-foreground">{t(`features.${row.feature}.description`)}</span>
                    </div>
                    {row.cells.map((cell) => (
                      <div role="cell" key={cell.action} className="flex justify-center py-1.5">
                        {cell.applies ? (
                          <button
                            type="button"
                            role="switch"
                            aria-checked={cell.on}
                            aria-label={t("permissions.cellLabel", { area, action: t(`actions.${cell.action}`) })}
                            disabled={disabled}
                            onClick={() => onToggle(row.feature, cell.action, !cell.on)}
                            className={cn(
                              "inline-flex h-11 w-11 items-center justify-center rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60",
                              cell.on
                                ? "border-primary bg-primary text-primary-foreground hover:bg-primary/90"
                                : "border-border bg-card text-transparent hover:border-primary",
                            )}
                          >
                            <Check className="h-[18px] w-[18px]" aria-hidden="true" />
                          </button>
                        ) : (
                          <span className="inline-flex h-11 w-11 items-center justify-center text-muted-foreground/50" title={t("permissions.legendNa")}>
                            <Minus className="h-4 w-4" aria-hidden="true" />
                            <span className="sr-only">{t("permissions.legendNa")}</span>
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
          </div>
        ))}
      </div>
    </div>
  );
}
